import { Injectable, inject } from '@angular/core';
import { RolloutStore } from './rollout.store';
import { ErrorItem, RolloutFormData, RolloutLogResponse } from './rollout.interface';
import { RolloutStatus } from './rollout.const';
import { RolloutBehaviorSelection } from '../rollout-behavior/rollout-behavior.interface';

/**
 * Facade for RolloutStore.
 * Exposes a clean, type-safe API for rollout state and actions.
 * Follows SOLID, Clean Code, and Angular best practices.
 */
@Injectable({ providedIn: 'root' })
export class RolloutFacade {
  #store = inject(RolloutStore);

  // Readonly signals from store
  readonly asyncTaskResource = this.#store.asyncTaskResource;
  /** Start date for rollout (readonly signal) */
  readonly startDate = this.#store.startDate;

  /** End date for rollout (readonly signal) */
  readonly endDate = this.#store.endDate;

  /** Selected branch for rollout (readonly signal) */
  readonly selectedBranch = this.#store.selectedBranch;

  /** Selected branchUId for rollout (readonly signal) */
  readonly selectedBranchUid = this.#store.selectedBranchUid;

  /** Selected reference logs UID for detailed error viewing (readonly signal) */
  readonly selectedReferenceLogsUId = this.#store.selectedReferenceLogsUId;

  /** Selected week for contributor view rollout (readonly signal) */
  readonly selectedWeek = this.#store.selectedWeek;

  /** Rollout behavior setting (readonly signal) */
  readonly rolloutBehavior = this.#store.rolloutBehavior;

  /** Whether this is contributor view mode (readonly signal) */
  readonly isContributorView = this.#store.isContributorView;

  /** Loading state (readonly signal) */
  readonly loading = this.#store.loading;

  /** Error message (readonly signal) */
  readonly error = this.#store.error;
  readonly isError = this.#store.isError;

  /** Rollout status (readonly signal) */
  readonly status = this.#store.status;

  /** Whether form is valid for rollout (readonly computed signal) */
  readonly isFormValid = this.#store.isFormValid;

  /** Whether form is valid for contributor view rollout (readonly computed signal) */
  readonly isContributorFormValid = this.#store.isContributorFormValid;

  /** Start month if start date is first of month (readonly computed signal) */
  readonly startMonth = this.#store.startMonth;

  /** End month if end date is last of month (readonly computed signal) */
  readonly endMonth = this.#store.endMonth;

  /** Minimum allowed start date for contributor rollout (readonly signal) */
  readonly minStartDate = this.#store.minStartDate;

  /** Maximum allowed end date for contributor rollout (readonly signal) */
  readonly maxEndDate = this.#store.maxEndDate;

  /** Complete rollout request data (readonly computed signal) */
  readonly rolloutRequestData = this.#store.rolloutRequestData;

  /** Available branches (readonly signal) */
  readonly branches = this.#store.branches;
  readonly branchesWithRolloutStatus = this.#store.branchesWithRolloutStatus;

  /** Branch short names as strings (readonly computed signal) */
  readonly branchNames = this.#store.branchNames;

  /** Count of available branches (readonly computed signal) */
  readonly branchesCount = this.#store.branchesCount;
  /** Count of valid weeks (readonly computed signal) */
  readonly validWeeks = this.#store.validWeeks;
  reloadAsyncTaskResource = () => {
    this.#store.reload();
  };
  // Action methods
  loadBranches() {
    this.#store.loadBranches();
  }
  /**
   * Updates the start date for rollout
   * @param startDate - The new start date
   */
  updateStartDate(startDate: Date | null): void {
    this.#store.updateStartDate(startDate);
  }

  /**
   * Updates the end date for rollout
   * @param endDate - The new end date
   */
  updateEndDate(endDate: Date | null): void {
    this.#store.updateEndDate(endDate);
  }

  /**
   * Updates the selected branch for rollout
   * @param selectedBranch - The new selected branch
   */
  updateSelectedBranch(selectedBranch: string | null, uId: string): void {
    this.#store.updateSelectedBranch(selectedBranch, uId);
  }

  /**
   * Updates the selected week for contributor view rollout
   * @param selectedWeek - The new selected week (1-6)
   */
  updateSelectedWeek(selectedWeek: number | null): void {
    this.#store.updateSelectedWeek(selectedWeek);
  }

  /**
   * Updates the selected reference logs UID for detailed error viewing
   * @param selectedReferenceLogsUId - The reference logs UID to track
   */
  updateSelectedReferenceLogsUId(selectedReferenceLogsUId: string | null): void {
    this.#store.updateSelectedReferenceLogsUId(selectedReferenceLogsUId);
  }

  /**
   * Updates the rollout behavior setting
   * @param rolloutBehavior - What the rollout overwrites and what it keeps
   */
  updateRolloutBehavior(rolloutBehavior: RolloutBehaviorSelection): void {
    this.#store.updateRolloutBehavior(rolloutBehavior);
  }

  /**
   * Updates the contributor view mode
   * @param isContributorView - Whether this is contributor view mode
   */
  updateIsContributorView(isContributorView: boolean): void {
    this.#store.updateIsContributorView(isContributorView);
  }

  /**
   * Updates both start and end dates from form data
   * @param formData - Form data containing both dates
   */
  updateFormData(formData: RolloutFormData): void {
    this.#store.updateFormData(formData);
  }
  /** Update valid weeks
   * @param validWeeks - Array of valid week strings
   */
  updateValidWeeks(validWeeks: string[]): void {
    this.#store.updateValidWeeks(validWeeks);
  }

  /**
   * Sets loading state
   * @param loading - Loading state
   */
  setLoading(loading: boolean): void {
    this.#store.setLoading(loading);
  }

  /**
   * Sets error state
   * @param error - Error message
   */
  setError(error: ErrorItem[] | null): void {
    this.#store.setError(error);
  }

  /**
   * Sets success state
   */
  setSuccess(): void {
    this.#store.setSuccess();
  }

  /**
   * Resets the store to initial state
   */
  reset(): void {
    this.#store.reset();
  }

  /**
   * Clears error state
   */
  clearError(): void {
    this.#store.clearError();
  }

  /**
   * Start polling for async task logs
   * @param taskUId - The task UID to poll for
   */
  startPolling(taskUId: string): void {
    this.#store.startPolling(taskUId);
  }

  /**
   * Stop polling for async task logs
   */
  stopPolling(): void {
    this.#store.stopPolling();
  }

  /**
   * Executes rollout operation (placeholder for future backend integration)
   * @param request - Rollout request data
   * @returns Promise resolving when rollout completes
   */
  executeBranchRollout() {
    this.setLoading(true);
    this.#store.branchRollout();
  }
  executeContributorRollout(contributorUId: string, scheduleUId: string, workScheduleUid: string) {
    this.setLoading(true);
    this.#store.contributorRollout({ contributorUId, scheduleUId, workScheduleUid });
  }

  /**
   * Updates the rollout status for a single branch by its uId
   * This is a simple method that updates only the specified branch's status
   * @param branchUId - The unique identifier of the branch to update
   * @param newStatus - The new rollout status to set
   */
  updateBranchRolloutStatus(branchUId: string, newStatus: RolloutStatus): void {
    this.#store.updateBranchRolloutStatus(branchUId, newStatus);
  }

  /**
   * Updates branch rollout status and extracts error data from completion event
   * @param branchUId - The unique identifier of the branch to update
   * @param newStatus - The new rollout status to set
   * @param logResponse - Optional log response containing error details
   */
  updateBranchRolloutStatusWithErrors(
    branchUId: string,
    newStatus: RolloutStatus,
    logResponse?: RolloutLogResponse,
  ): void {
    this.#store.updateBranchRolloutStatusWithErrors(branchUId, newStatus, logResponse);
  }

  /**
   * Initialize the store with date constraints for contributor rollout
   * @param minStartDate - The earliest allowed start date
   * @param maxEndDate - The latest allowed end date
   */
  initializeWithDateConstraints(minStartDate: Date | null, maxEndDate: Date | null): void {
    this.#store.initializeWithDateConstraints(minStartDate, maxEndDate);
  }
}
